import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, TemplateRef, ViewChild, inject } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AbilityService } from 'ability';
import { I18NextPipe } from 'angular-i18next';
import {
  AlertComponent,
  ButtonComponent,
  FormGroupComponent,
  MbsPopupType,
  ModalComponent,
  ModalFooterDirective,
  ModalService,
  ModalSettings,
  RadioComponent
} from 'mbs-ui-kit';
import { StorageType } from 'mbs-ui-shared';
import { HelpLinkDirective } from 'mbs-ui-shared@help/helpLink.directive';
import { StorageAccountsService } from 'mbs-ui-shared@services/storage-accounts/storage-accounts.service';
import { finalize, noop } from 'rxjs';

enum PeriodVariants {
  WasabiSmall = 30,
  WasabiBig = 90
}

@UntilDestroy()
@Component({
  templateUrl: './wasabi-minimum-retention-period.component.html',
  imports: [
    CommonModule,
    ButtonComponent,
    ReactiveFormsModule,
    HelpLinkDirective,
    FormGroupComponent,
    AlertComponent,
    I18NextPipe,
    ModalComponent,
    RadioComponent,
    ModalFooterDirective
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WasabiMinimumRetentionPeriodComponent implements OnInit {
  ability = inject(AbilityService);
  private cdr = inject(ChangeDetectorRef);
  private storageAccounts = inject(StorageAccountsService);
  private modalService = inject(ModalService);
  private i18N = inject(I18NextPipe);

  public readonly periodVariants = PeriodVariants;
  public readonly mbsPopupType = MbsPopupType;
  public storageType: StorageType;
  public storageAccountId: string;
  public durationControl = new FormControl(null);
  public loading = false;

  @ViewChild(ModalComponent, { static: true }) baseModal: ModalComponent;
  @ViewChild('warningModalTemplate', { static: true }) private warningModalTemplate: TemplateRef<any>;

  ngOnInit(): void {
    this.storageAccountId = this.baseModal.data.storageAccountId;
    this.loading = true;
    this.storageAccounts
      .getMinimumStorageDurationForWasabi(this.storageAccountId)
      .pipe(
        untilDestroyed(this),
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();

          this.durationControl.valueChanges.pipe(untilDestroyed(this)).subscribe((val) => {
            val === PeriodVariants.WasabiSmall && this.showWarningModal();
          });
        })
      )
      .subscribe((result: { duration: number }) => {
        this.durationControl.patchValue(result?.duration ? result.duration : PeriodVariants.WasabiBig);
      });
  }

  showWarningModal() {
    const settings: ModalSettings = {
      header: {
        title: this.i18N.transform('shared:wasabiMinimumRetentionPeriod.alertModalTitle')
      },
      footer: {
        okButton: {
          text: this.i18N.transform('shared:buttons.confirm')
        }
      }
    };

    this.modalService
      .open(settings, this.warningModalTemplate)
      .then((res) => {
        res && this.selectClickHandler();
      })
      .catch(noop);
  }

  selectClickHandler(): void {
    this.loading = true;
    this.cdr.detectChanges();
    this.storageAccounts
      .minimumStorageDurationForWasabi(this.storageAccountId, this.durationControl.value)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: () => {
          this.loading = false;
          this.baseModal.save(true);
        },
        error: () => {
          this.loading = false;
          this.baseModal.close();
        }
      });
  }

  cancelClickHandler(): void {
    this.baseModal.close();
  }
}
